/**
 * PurchaseCenterPage — iter125
 *
 * Zentraler „Meine Käufe"-Bereich gemäß Spec §2.
 *
 * Tabs:
 *   - Alle Käufe        (aggregierte Ansicht, sortiert nach Kaufdatum)
 *   - Social-Inhalte    (digitale SOCIAL-Käufe via /api/me/purchases/social)
 *   - Marketplace       (physische MP-Bestellungen aus /api/orders/buyer)
 *   - StillTouchedX     (Sub-Tabs: Physische STX-Bestellungen · Digitale STX-Inhalte)
 *
 * Digitale STX/Social-Käufe werden strikt getrennt gehalten (Spec §7).
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { TabBar } from "@/components/toggles";
import { ShoppingBag, Loader2, Download, DownloadCloud, Eye, Package, Sparkles, FileImage, EyeOff, Lock, CheckCircle, XCircle, AlertOctagon, Clock, ArrowLeft } from "lucide-react";
// iter238 · Paid-Media-Download-Button für PPV-Käufer
import PaidMediaDownloadButton from "@/components/paid-media/PaidMediaDownloadButton";

const TOP_TABS = [
  { key: "all",         labelKey: "purchases.tab_all",         labelDefault: "Alle Käufe",       testId: "purch-tab-all" },
  { key: "social",      labelKey: "purchases.tab_social",      labelDefault: "Social-Inhalte",   testId: "purch-tab-social" },
  { key: "marketplace", labelKey: "purchases.tab_marketplace", labelDefault: "Marketplace",      testId: "purch-tab-marketplace" },
  { key: "stx",         labelKey: "purchases.tab_stx",         labelDefault: "stilltouchedX",    testId: "purch-tab-stx" },
];

const SOCIAL_SUB_TABS = [
  { key: "social_purchases", labelKey: "purchases.social_purchases", labelDefault: "Käufe",        testId: "purch-social-sub-purchases" },
  { key: "social_abos",      labelKey: "purchases.social_abos",      labelDefault: "Abonnements",  testId: "purch-social-sub-abos" },
];

const STX_SUB_TABS = [
  { key: "stx_physical", labelKey: "purchases.stx_physical", labelDefault: "Physische STX-Bestellungen", testId: "purch-stx-sub-physical" },
  { key: "stx_digital",  labelKey: "purchases.stx_digital",  labelDefault: "Digitale STX-Inhalte",       testId: "purch-stx-sub-digital" },
];

function fmtEuro(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(2)} €`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return ""; }
}

export default function PurchaseCenterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const urlSub = searchParams.get("sub");
  const tab = TOP_TABS.some((tt) => tt.key === urlTab) ? urlTab : "all";
  const stxSub = STX_SUB_TABS.some((st) => st.key === urlSub) ? urlSub : "stx_physical";
  const socialSub = SOCIAL_SUB_TABS.some((st) => st.key === urlSub) ? urlSub : "social_purchases";

  const setTab = (k) => {
    const p = new URLSearchParams(searchParams);
    if (k === "all") p.delete("tab"); else p.set("tab", k);
    if (k !== "stx" && k !== "social") p.delete("sub");
    setSearchParams(p, { replace: true });
  };
  const setStxSub = (k) => {
    const p = new URLSearchParams(searchParams);
    p.set("tab", "stx");
    p.set("sub", k);
    setSearchParams(p, { replace: true });
  };
  const setSocialSub = (k) => {
    const p = new URLSearchParams(searchParams);
    p.set("tab", "social");
    p.set("sub", k);
    setSearchParams(p, { replace: true });
  };

  const [ordersMp, setOrdersMp] = useState([]);
  const [ordersStx, setOrdersStx] = useState([]);
  const [social, setSocial] = useState([]);
  const [stxDigital, setStxDigital] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        api.get("/orders/buyer"),
        api.get("/me/purchases/social"),
        api.get("/me/purchases/stx"),
        api.get("/me/subscriptions"),
      ]);
      if (results[0].status === "fulfilled") {
        const list = results[0].value.data?.orders || results[0].value.data || [];
        setOrdersMp(list.filter((o) => (o.area_scope || "").toUpperCase() === "MARKETPLACE"));
        setOrdersStx(list.filter((o) => (o.area_scope || "").toUpperCase() === "STILLTOUCHEDX"));
      }
      if (results[1].status === "fulfilled") setSocial(results[1].value.data?.purchases || []);
      if (results[2].status === "fulfilled") setStxDigital(results[2].value.data?.purchases || []);
      if (results[3].status === "fulfilled") setSubs(results[3].value.data?.subscriptions || []);
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    loadAll();
  }, [user, navigate, loadAll]);

  // Aggregierte Liste (Alle-Käufe): normalisiert für einheitliche Card
  const allItems = useMemo(() => {
    const rows = [];
    ordersMp.forEach((o) => rows.push({
      key: `mp-${o.order_id || o.id}`, kind: "MP", ts: o.created_at || o.purchased_at,
      title: o.listing_title || o.title || o.public_order_no || o.order_id?.slice(-8),
      subtitle: o.public_order_no ? `MP · ${o.public_order_no}` : "Marketplace",
      price: o.total, status: o.status,
      link: `/profile/purchases/${o.order_id || o.id}`,
    }));
    ordersStx.forEach((o) => rows.push({
      key: `stxo-${o.order_id || o.id}`, kind: "STX_PHYS", ts: o.created_at || o.purchased_at,
      title: o.listing_title || o.title || o.public_order_no,
      subtitle: `STX · Physisch · ${o.public_order_no || ""}`,
      price: o.total, status: o.status,
      link: `/profile/purchases/${o.order_id || o.id}`,
    }));
    social.forEach((s) => rows.push({
      key: `soc-${s.entitlement_id}`, kind: "SOCIAL", ts: s.granted_at,
      title: s.asset?.title || s.resource_id, subtitle: `Social · ${s.creator?.display_name || s.creator?.handle || ""}`,
      price: s.asset?.price_eur, status: s.entitlement_status,
      thumb: s.asset?.thumbnail_url || s.asset?.media_url,
      // iter264 · Klick öffnet die Kaufübersicht (Detailseite) des Social-Kaufs.
      link: `/profile/purchases/social/${s.purchase_id}`,
      entitlement: s,
    }));
    stxDigital.forEach((s) => rows.push({
      key: `stxd-${s.entitlement_id}`, kind: "STX_DIG", ts: s.granted_at,
      title: s.resource_id, subtitle: `STX · Digital · ${s.stx_order?.public_order_no || ""}`,
      status: s.release_status,
      // iter270 · Klick öffnet die verknüpfte STX-Bestellübersicht (falls vorhanden),
      // sonst Fallback in den STX-Digital-Tab.
      link: s.stx_order_id
        ? `/profile/purchases/${s.stx_order_id}`
        : "/profile/purchases?tab=stx&sub=stx_digital",
      entitlement: s,
    }));
    // iter270 · Creator-/Premium-Abos in „Alle Käufe" einreihen (klickbar).
    subs.forEach((s) => rows.push({
      key: `abo-${s.subscription_id}`, kind: "ABO", ts: s.started_at,
      title: s.plan?.title || s.tier || "Premium-Abo",
      subtitle: `Abo · ${s.creator?.display_name || s.creator?.handle || ""}`,
      price: s.price_snapshot ?? s.plan?.price_monthly_eur, status: s.status,
      thumb: s.creator?.avatar_url,
      link: `/profile/purchases/abo/${s.subscription_id}`,
    }));
    rows.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
    return rows;
  }, [ordersMp, ordersStx, social, stxDigital, subs]);

  return (
    <div data-testid="purchase-center" className="w-full max-w-3xl 2xl:max-w-full 2xl:px-6 mx-auto 2xl:mx-0 py-6">
      <div className="flex items-center gap-3 px-4 mb-4">
        <button data-testid="purchases-back" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2 flex-1">
          <ShoppingBag size={22} /> {t("purchases.title", { defaultValue: "Meine Käufe" })}
        </h1>
      </div>

      <div className="px-2 mb-3">
        <TabBar
          tabs={TOP_TABS.map((tt) => ({ key: tt.key, label: t(tt.labelKey, { defaultValue: tt.labelDefault }), testId: tt.testId }))}
          activeKey={tab}
          onChange={setTab}
        />
      </div>

      {tab === "stx" && (
        <div className="px-2 mb-3">
          <TabBar
            tabs={STX_SUB_TABS.map((st) => ({ key: st.key, label: t(st.labelKey, { defaultValue: st.labelDefault }), testId: st.testId }))}
            activeKey={stxSub}
            onChange={setStxSub}
          />
        </div>
      )}

      {tab === "social" && (
        <div className="px-2 mb-3">
          <TabBar
            tabs={SOCIAL_SUB_TABS.map((st) => ({ key: st.key, label: t(st.labelKey, { defaultValue: st.labelDefault }), testId: st.testId }))}
            activeKey={socialSub}
            onChange={setSocialSub}
          />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-[#999]" /></div>
      ) : tab === "all" ? (
        <PurchaseList items={allItems} onOpen={(i) => i.link && navigate(i.link)} emptyLabel={t("purchases.empty_all", { defaultValue: "Noch keine Käufe." })} />
      ) : tab === "social" && socialSub === "social_abos" ? (
        <PurchaseList
          items={subs.map((s) => ({
            key: `abo-${s.subscription_id}`, kind: "ABO", ts: s.started_at,
            title: s.plan?.title || s.tier || "Premium-Abo",
            subtitle: `Abo · ${s.creator?.display_name || s.creator?.handle || ""}`,
            amount: s.price_snapshot, link: `/profile/purchases/abo/${s.subscription_id}`,
          }))}
          onOpen={(i) => i.link && navigate(i.link)}
          emptyLabel={t("purchases.empty_social_abos", { defaultValue: "Noch keine Abonnements." })}
        />
      ) : tab === "social" ? (
        <DigitalList items={social} area="SOCIAL" navigate={navigate} emptyLabel={t("purchases.empty_social", { defaultValue: "Noch keine digitalen Social-Käufe." })} />
      ) : tab === "marketplace" ? (
        <OrderList orders={ordersMp} navigate={navigate} emptyLabel={t("purchases.empty_mp", { defaultValue: "Noch keine Marketplace-Bestellungen." })} />
      ) : tab === "stx" && stxSub === "stx_physical" ? (
        <OrderList orders={ordersStx} navigate={navigate} emptyLabel={t("purchases.empty_stx_phys", { defaultValue: "Noch keine physischen STX-Bestellungen." })} />
      ) : (
        <DigitalList items={stxDigital} area="STX" navigate={navigate} onRefresh={loadAll} emptyLabel={t("purchases.empty_stx_dig", { defaultValue: "Noch keine digitalen STX-Inhalte." })} />
      )}
    </div>
  );
}

// ── Sub-Components ────────────────────────────────────
function PurchaseList({ items, onOpen, emptyLabel }) {
  if (items.length === 0) {
    return <p data-testid="purch-empty" className="text-center text-sm text-[#999] py-16">{emptyLabel}</p>;
  }
  return (
    <ul className="flex flex-col gap-2 px-4">
      {items.map((it, idx) => (
        <li key={`${it.key}-${idx}`}>
          <button
            data-testid={`purch-item-${it.key}`}
            onClick={() => onOpen(it)}
            className="w-full flex items-center gap-3 border border-[#E5E5E5] rounded-2xl p-3 hover:bg-[#FAFAFA] transition-colors text-left"
          >
            <div className="w-12 h-12 rounded bg-[#F5F5F5] overflow-hidden shrink-0 flex items-center justify-center">
              {it.thumb ? <img src={it.thumb} alt="" className="w-full h-full object-cover" /> : <KindIcon kind={it.kind} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0A0A0A] truncate">{it.title || "—"}</p>
              <p className="text-xs text-[#666] truncate">{it.subtitle}</p>
              <p className="text-[10px] text-[#999] mt-0.5">{fmtDate(it.ts)}</p>
            </div>
            <div className="text-right">
              {it.price != null && <p className="text-sm font-bold text-[#0A0A0A]">{fmtEuro(it.price)}</p>}
              <p className="text-[10px] text-[#999]">{it.status || ""}</p>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function KindIcon({ kind }) {
  if (kind === "SOCIAL") return <Sparkles size={16} className="text-[#0A0A0A]" />;
  if (kind === "STX_DIG") return <FileImage size={16} className="text-[#0A0A0A]" />;
  return <Package size={16} className="text-[#0A0A0A]" />;
}

function DigitalList({ items, area, navigate, emptyLabel, onRefresh }) {
  const [localState, setLocalState] = useState({});
  if (items.length === 0) {
    return <p data-testid={`purch-digital-empty-${area}`} className="text-center text-sm text-[#999] py-16">{emptyLabel}</p>;
  }
  // iter262b · Ausblenden-Funktion entfernt (Spec: kein Ausblenden-Button).
  // iter242 · lokaler Merge, damit die Badge nach dem Download sofort auf
  // DELIVERED umschaltet, auch bevor `onRefresh()` den Server-State neu lädt.
  const applyLocal = (purchaseId, patch) => {
    setLocalState((prev) => ({ ...prev, [purchaseId]: { ...(prev[purchaseId] || {}), ...patch } }));
  };
  const mergeItem = (it) => ({ ...it, ...(localState[it.purchase_id] || {}) });
  return (
    <ul className="grid gap-3 px-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
      {items.map((raw, idx) => {
        const it = mergeItem(raw);
        // iter238 · Social-Käufe haben ein `purchase_id` + Delivery-State,
        // STX-Käufe nutzen weiter das entitlement_id-Modell.
        const isSocial = area === "SOCIAL" && !!it.purchase_id;
        const revoked = it.purchase_state === "REVOKED_LEGAL_TAKEDOWN";
        const refunded = it.purchase_state === "REFUNDED";
        return (
          <li key={`${it.entitlement_id || it.purchase_id}-${idx}`}>
            <div data-testid={`purch-digital-${it.entitlement_id || it.purchase_id}`} className="border border-[#E5E5E5] rounded-2xl overflow-hidden bg-white flex flex-col">
              <div className="aspect-video bg-[#F5F5F5] flex items-center justify-center relative">
                {it.asset?.thumbnail_url || it.asset?.media_url ? (
                  <img src={it.asset.thumbnail_url || it.asset.media_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <FileImage size={28} className="text-[#CCC]" />
                )}
                {revoked && (
                  <div className="absolute inset-0 bg-red-500/80 text-white text-xs flex items-center justify-center flex-col gap-1 font-medium">
                    <Lock size={16} />
                    Zugriff entzogen
                  </div>
                )}
              </div>
              <div className="p-3 flex-1 flex flex-col gap-1">
                <p className="text-sm font-semibold text-[#0A0A0A] line-clamp-1">{it.asset?.title || it.resource_id}</p>
                {it.creator?.display_name && <p className="text-xs text-[#666]">{it.creator.display_name}</p>}
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {(() => {
                    // iter244 · Klarer Badge-Split: unterschiedliche Farbfamilien +
                    // Icons für DOWNLOAD_AVAILABLE, DELIVERED, RENDERING, REVOKED, REFUNDED, PAID.
                    let label, cls, Icon, title;
                    if (revoked) {
                      label = "Zugriff entzogen"; cls = "border-red-300 text-red-700 bg-red-50";
                      Icon = XCircle; title = "Kauf aus rechtlichen Gründen entzogen";
                    } else if (refunded) {
                      label = "Erstattet"; cls = "border-amber-300 text-amber-700 bg-amber-50";
                      Icon = AlertOctagon; title = "Kauf erstattet";
                    } else if (isSocial && it.purchase_state === "DELIVERED") {
                      label = "Ausgeliefert"; cls = "border-emerald-400 text-emerald-800 bg-emerald-50";
                      Icon = CheckCircle; title = "Persönliche Kopie erfolgreich heruntergeladen";
                    } else if (isSocial && it.purchase_state === "DOWNLOAD_AVAILABLE") {
                      label = "Bereit"; cls = "border-indigo-400 text-indigo-700 bg-indigo-50";
                      Icon = DownloadCloud; title = "Deine persönliche Kopie steht zum Download bereit";
                    } else if (isSocial && (it.delivery_render_status === "RENDERING" || it.delivery_render_status === "PENDING")) {
                      label = "Wird vorbereitet"; cls = "border-slate-300 text-slate-700 bg-slate-50";
                      Icon = Clock; title = "Deine persönliche Kopie wird gerade erstellt";
                    } else if (isSocial) {
                      label = "Gekauft"; cls = "border-green-300 text-green-700 bg-green-50";
                      Icon = ShoppingBag; title = "Kauf abgeschlossen";
                    } else {
                      label = it.release_status || "AVAILABLE";
                      cls = it.release_status === "PENDING"
                        ? "border-amber-300 text-amber-700 bg-amber-50"
                        : "border-green-300 text-green-700 bg-green-50";
                      Icon = null; title = "";
                    }
                    return (
                      <span
                        data-testid={`purch-badge-${it.purchase_id || it.entitlement_id}`}
                        title={title}
                        className={`text-[10px] px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${cls}`}
                      >
                        {Icon && <Icon size={10} />} {label}
                      </span>
                    );
                  })()}
                  {it.stx_order?.public_order_no && (
                    <span className="text-[10px] text-[#999]">→ {it.stx_order.public_order_no}</span>
                  )}
                </div>
                <p className="text-[10px] text-[#999] mt-1">{fmtDate(it.granted_at || it.purchased_at)}</p>
                <div className="flex flex-col gap-2 mt-2 pt-2 border-t border-[#F0F0F0]">
                  {isSocial ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <PaidMediaDownloadButton
                        purchase={it}
                        onUpdate={(patch) => {
                          applyLocal(it.purchase_id, patch);
                          if (patch?.status === "READY" && patch?.delivered_at) {
                            // Nach kurzem Delay serverseitigen State frisch holen.
                            setTimeout(() => onRefresh?.(), 2000);
                          }
                        }}
                      />
                      <button
                        data-testid={`purch-detail-${it.purchase_id}`}
                        onClick={() => navigate(`/profile/purchases/social/${it.purchase_id}`)}
                        className="h-9 px-4 rounded-full border border-[#E5E5E5] text-[#0A0A0A] text-xs flex items-center gap-2 hover:bg-[#F5F5F5]"
                      >
                        Kaufübersicht
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {it.stx_order_id && (
                        <button
                          data-testid={`purch-detail-stx-${it.entitlement_id}`}
                          onClick={() => navigate(`/profile/purchases/${it.stx_order_id}`)}
                          className="h-9 px-4 rounded-full border border-[#E5E5E5] text-[#0A0A0A] text-xs flex items-center justify-center gap-2 hover:bg-[#F5F5F5]"
                        >
                          Kaufübersicht
                        </button>
                      )}
                      <div className="flex gap-2">
                      <button
                        data-testid={`purch-view-${it.entitlement_id}`}
                        disabled={!it.download_allowed}
                        onClick={() => navigate(`/entitlements/${it.entitlement_id}`)}
                        className="flex-1 h-8 text-xs bg-[#F5F5F5] hover:bg-[#E5E5E5] disabled:opacity-40 flex items-center justify-center gap-1"
                      >
                        <Eye size={12} /> Ansehen
                      </button>
                      <button
                        data-testid={`purch-download-${it.entitlement_id}`}
                        disabled={!it.download_allowed}
                        onClick={async () => {
                          try {
                            const { data } = await (await import("@/lib/api")).default.post(
                              `/entitlements/${it.entitlement_id}/download-token`
                            );
                            if (data?.download_url) {
                              const base = process.env.REACT_APP_BACKEND_URL || "";
                              window.open(`${base}${data.download_url}`, "_blank");
                            }
                          } catch (err) {
                            window.alert(err?.response?.data?.detail || "Download nicht möglich");
                          }
                        }}
                        className="flex-1 h-8 text-xs bg-[#0A0A0A] text-white hover:bg-[#222] disabled:opacity-40 flex items-center justify-center gap-1"
                      >
                        <Download size={12} /> DL
                      </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function OrderList({ orders, navigate, emptyLabel }) {
  if (orders.length === 0) {
    return <p data-testid="purch-order-empty" className="text-center text-sm text-[#999] py-16">{emptyLabel}</p>;
  }
  return (
    <ul className="flex flex-col gap-2 px-4">
      {orders.map((o) => (
        <li key={o.order_id || o.id}>
          <button
            data-testid={`purch-order-${o.order_id || o.id}`}
            onClick={() => navigate(`/profile/purchases/${o.order_id || o.id}`)}
            className="w-full flex items-center gap-3 border border-[#E5E5E5] rounded-2xl p-3 hover:bg-[#FAFAFA] transition-colors text-left"
          >
            <div className="w-12 h-12 rounded bg-[#F5F5F5] overflow-hidden shrink-0">
              {o.listing_image_url && <img src={o.listing_image_url} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0A0A0A] truncate">{o.listing_title || o.public_order_no || o.order_id?.slice(-8)}</p>
              <p className="text-xs text-[#666]">{o.public_order_no}</p>
              <p className="text-[10px] text-[#999]">{fmtDate(o.created_at || o.purchased_at)}</p>
            </div>
            <div className="text-right">
              {o.total != null && <p className="text-sm font-bold text-[#0A0A0A]">{fmtEuro(o.total)}</p>}
              <p className="text-[10px] text-[#999]">{o.status}</p>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}
